import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@/presentation/components/common';
import { useToast } from '@/presentation/contexts/ToastContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';

export type PeriodOpsConfig = {
  periodoCierreEnabled: boolean;
  periodoCierreDia: number;
  periodoCierreHora: number;
  periodoCierreMinuto: number;
  periodoCierreLastRunAt: string | null;
  desactivarImpagasEnabled: boolean;
  desactivarImpagasDia: number;
  desactivarImpagasHora: number;
  desactivarImpagasMinuto: number;
  desactivarImpagasNotifyWhatsApp: boolean;
  desactivarImpagasLastRunAt: string | null;
  ventanaPagoDiaCierre: number;
  timezone: string;
  updatedAt: string | null;
};

export const DEFAULT_PERIOD_OPS_CONFIG: PeriodOpsConfig = {
  periodoCierreEnabled: true,
  periodoCierreDia: 1,
  periodoCierreHora: 8,
  periodoCierreMinuto: 0,
  periodoCierreLastRunAt: null,
  desactivarImpagasEnabled: true,
  desactivarImpagasDia: 10,
  desactivarImpagasHora: 9,
  desactivarImpagasMinuto: 0,
  desactivarImpagasNotifyWhatsApp: true,
  desactivarImpagasLastRunAt: null,
  ventanaPagoDiaCierre: 10,
  timezone: 'America/Argentina/Buenos_Aires',
  updatedAt: null,
};

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

export function periodOpsTimeValue(hour: number, minute: number): string {
  return `${pad2(hour)}:${pad2(minute)}`;
}

function parseTime(value: string): { hour: number; minute: number } {
  const [h, m] = value.split(':').map(Number);
  return {
    hour: Number.isFinite(h) ? Math.min(23, Math.max(0, h)) : 0,
    minute: Number.isFinite(m) ? Math.min(59, Math.max(0, m)) : 0,
  };
}

const fieldControlClass =
  'block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white';

const fieldLabelClass = 'mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300';

function SectionCard({
  title,
  description,
  children,
  tone = 'default',
}: {
  title: string;
  description: string;
  children: React.ReactNode;
  tone?: 'default' | 'danger';
}) {
  const frame =
    tone === 'danger' ? 'border-rose-200 dark:border-rose-900/50' : '';
  return (
    <section className={`panel-card p-5 sm:p-6 ${frame}`}>
      <header className="mb-5">
        <h2 className="text-base font-semibold tracking-tight text-slate-900 dark:text-white">
          {title}
        </h2>
        <p className="mt-1.5 max-w-xl text-sm leading-relaxed text-slate-500 dark:text-slate-400">
          {description}
        </p>
      </header>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

function SwitchRow({
  title,
  description,
  enabled,
  onLabel,
  offLabel,
  disabled,
  onToggle,
  plain = false,
}: {
  title: string;
  description: string;
  enabled: boolean;
  onLabel: string;
  offLabel: string;
  disabled: boolean;
  onToggle: () => void;
  plain?: boolean;
}) {
  return (
    <div
      className={
        plain
          ? 'flex items-start justify-between gap-4'
          : 'flex items-start justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5 dark:border-gray-700 dark:bg-gray-900/50'
      }
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-slate-900 dark:text-white">{title}</p>
          <span
            className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
              enabled
                ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200'
                : 'bg-slate-200 text-slate-600 dark:bg-gray-700 dark:text-slate-300'
            }`}
          >
            {enabled ? onLabel : offLabel}
          </span>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
          {description}
        </p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        disabled={disabled}
        onClick={onToggle}
        className={`relative mt-0.5 inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-gray-800 ${
          enabled ? 'bg-teal-600' : 'bg-slate-300 dark:bg-gray-600'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
            enabled ? 'translate-x-4' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}

function DayTimeFields({
  dayLabel,
  timeLabel,
  day,
  hour,
  minute,
  dayOptions,
  disabled,
  onDayChange,
  onTimeChange,
}: {
  dayLabel: string;
  timeLabel: string;
  day: number;
  hour: number;
  minute: number;
  dayOptions: number[];
  disabled: boolean;
  onDayChange: (day: number) => void;
  onTimeChange: (hour: number, minute: number) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="w-full">
        <label className={fieldLabelClass}>{dayLabel}</label>
        <select
          disabled={disabled}
          className={fieldControlClass}
          value={day}
          onChange={(e) => onDayChange(Number(e.target.value))}
        >
          {dayOptions.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </div>
      <Input
        label={timeLabel}
        type="time"
        disabled={disabled}
        value={periodOpsTimeValue(hour, minute)}
        onChange={(e) => {
          const parsed = parseTime(e.target.value);
          onTimeChange(parsed.hour, parsed.minute);
        }}
      />
    </div>
  );
}

export const PeriodOpsConfigForm: React.FC<{ canEdit: boolean }> = ({ canEdit }) => {
  const { t } = useTranslation();
  const { success, error: showError } = useToast();
  const [config, setConfig] = useState<PeriodOpsConfig>(DEFAULT_PERIOD_OPS_CONFIG);
  const [draft, setDraft] = useState<PeriodOpsConfig>(DEFAULT_PERIOD_OPS_CONFIG);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await httpClient.get<PeriodOpsConfig>('/panel-config');
      const next = { ...DEFAULT_PERIOD_OPS_CONFIG, ...res.data };
      setConfig(next);
      setDraft(next);
    } catch {
      showError(t('pages.periodOps.configLoadError'));
    } finally {
      setIsLoading(false);
    }
  }, [showError, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = useMemo(() => {
    return (
      draft.periodoCierreEnabled !== config.periodoCierreEnabled ||
      draft.periodoCierreDia !== config.periodoCierreDia ||
      draft.periodoCierreHora !== config.periodoCierreHora ||
      draft.periodoCierreMinuto !== config.periodoCierreMinuto ||
      draft.desactivarImpagasEnabled !== config.desactivarImpagasEnabled ||
      draft.desactivarImpagasDia !== config.desactivarImpagasDia ||
      draft.desactivarImpagasHora !== config.desactivarImpagasHora ||
      draft.desactivarImpagasMinuto !== config.desactivarImpagasMinuto ||
      draft.desactivarImpagasNotifyWhatsApp !== config.desactivarImpagasNotifyWhatsApp ||
      draft.ventanaPagoDiaCierre !== config.ventanaPagoDiaCierre
    );
  }, [config, draft]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit) return;
    setIsSaving(true);
    try {
      const res = await httpClient.put<PeriodOpsConfig>('/panel-config', {
        periodoCierreEnabled: draft.periodoCierreEnabled,
        periodoCierreDia: draft.periodoCierreDia,
        periodoCierreHora: draft.periodoCierreHora,
        periodoCierreMinuto: draft.periodoCierreMinuto,
        desactivarImpagasEnabled: draft.desactivarImpagasEnabled,
        desactivarImpagasDia: draft.desactivarImpagasDia,
        desactivarImpagasHora: draft.desactivarImpagasHora,
        desactivarImpagasMinuto: draft.desactivarImpagasMinuto,
        desactivarImpagasNotifyWhatsApp: draft.desactivarImpagasNotifyWhatsApp,
        ventanaPagoDiaCierre: draft.ventanaPagoDiaCierre,
      });
      const next = { ...DEFAULT_PERIOD_OPS_CONFIG, ...res.data };
      setConfig(next);
      setDraft(next);
      success(t('pages.periodOps.configSaved'));
    } catch {
      showError(t('pages.periodOps.configSaveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const formatLastRun = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString() : t('pages.periodOps.neverRun');

  const dayOptions = Array.from({ length: 28 }, (_, i) => i + 1);
  const graceWarn = draft.desactivarImpagasDia < draft.ventanaPagoDiaCierre;
  const fieldsDisabled = isSaving || !canEdit;

  return (
    <form onSubmit={handleSave} className="flex min-h-0 flex-1 flex-col">
      {isLoading ? (
        <p className="text-sm text-slate-500">{t('common.loading')}</p>
      ) : (
        <>
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto pb-4">
            <SectionCard
              title={t('pages.periodOps.paymentWindowTitle')}
              description={t('pages.periodOps.paymentWindowHint')}
            >
              <div className="max-w-[12rem]">
                <Input
                  label={t('pages.periodOps.paymentWindowDay')}
                  type="number"
                  min={1}
                  max={28}
                  disabled={fieldsDisabled}
                  value={draft.ventanaPagoDiaCierre}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      ventanaPagoDiaCierre: Math.min(28, Math.max(1, Number(e.target.value) || 1)),
                    }))
                  }
                />
              </div>
            </SectionCard>

            <SectionCard
              title={t('pages.periodOps.closeAutoTitle')}
              description={t('pages.periodOps.closeAutoHint')}
            >
              <SwitchRow
                title={t('pages.periodOps.enableToggle')}
                description={t('pages.periodOps.timezoneNote', { tz: draft.timezone })}
                enabled={draft.periodoCierreEnabled}
                onLabel={t('pages.periodOps.autoOn')}
                offLabel={t('pages.periodOps.autoOff')}
                disabled={fieldsDisabled}
                onToggle={() =>
                  setDraft((prev) => ({
                    ...prev,
                    periodoCierreEnabled: !prev.periodoCierreEnabled,
                  }))
                }
              />
              {draft.periodoCierreEnabled ? (
                <div className="space-y-3 rounded-xl border border-slate-100 bg-slate-50/70 p-4 dark:border-gray-700 dark:bg-gray-900/40">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    {t('pages.periodOps.closeScheduleTitle')}
                  </p>
                  <DayTimeFields
                    dayLabel={t('pages.periodOps.dayOfMonth')}
                    timeLabel={t('pages.periodOps.time')}
                    day={draft.periodoCierreDia}
                    hour={draft.periodoCierreHora}
                    minute={draft.periodoCierreMinuto}
                    dayOptions={dayOptions}
                    disabled={fieldsDisabled}
                    onDayChange={(periodoCierreDia) =>
                      setDraft((prev) => ({ ...prev, periodoCierreDia }))
                    }
                    onTimeChange={(periodoCierreHora, periodoCierreMinuto) =>
                      setDraft((prev) => ({ ...prev, periodoCierreHora, periodoCierreMinuto }))
                    }
                  />
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {t('pages.periodOps.lastAutoRun')}: {formatLastRun(config.periodoCierreLastRunAt)}
                  </p>
                </div>
              ) : null}
            </SectionCard>

            <SectionCard
              title={t('pages.periodOps.deactivateAutoTitle')}
              description={t('pages.periodOps.deactivateAutoHint')}
              tone="danger"
            >
              <SwitchRow
                title={t('pages.periodOps.enableToggle')}
                description={
                  draft.desactivarImpagasEnabled
                    ? t('pages.periodOps.deactivateAutoOn')
                    : t('pages.periodOps.deactivateAutoOffNote')
                }
                enabled={draft.desactivarImpagasEnabled}
                onLabel={t('pages.periodOps.autoOn')}
                offLabel={t('pages.periodOps.autoOff')}
                disabled={fieldsDisabled}
                onToggle={() =>
                  setDraft((prev) => ({
                    ...prev,
                    desactivarImpagasEnabled: !prev.desactivarImpagasEnabled,
                  }))
                }
              />
              {draft.desactivarImpagasEnabled ? (
                <div className="space-y-4 rounded-xl border border-rose-100 bg-rose-50/50 p-4 dark:border-rose-900/40 dark:bg-rose-950/20">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                    {t('pages.periodOps.deactivateScheduleTitle')}
                  </p>
                  <DayTimeFields
                    dayLabel={t('pages.periodOps.dayOfMonth')}
                    timeLabel={t('pages.periodOps.time')}
                    day={draft.desactivarImpagasDia}
                    hour={draft.desactivarImpagasHora}
                    minute={draft.desactivarImpagasMinuto}
                    dayOptions={dayOptions}
                    disabled={fieldsDisabled}
                    onDayChange={(desactivarImpagasDia) =>
                      setDraft((prev) => ({ ...prev, desactivarImpagasDia }))
                    }
                    onTimeChange={(desactivarImpagasHora, desactivarImpagasMinuto) =>
                      setDraft((prev) => ({
                        ...prev,
                        desactivarImpagasHora,
                        desactivarImpagasMinuto,
                      }))
                    }
                  />
                  <SwitchRow
                    title={t('pages.periodOps.notifyWhatsAppTitle')}
                    description={t('pages.periodOps.notifyWhatsAppHint')}
                    enabled={draft.desactivarImpagasNotifyWhatsApp}
                    onLabel={t('pages.periodOps.notifyOn')}
                    offLabel={t('pages.periodOps.notifyOff')}
                    plain
                    disabled={fieldsDisabled}
                    onToggle={() =>
                      setDraft((prev) => ({
                        ...prev,
                        desactivarImpagasNotifyWhatsApp: !prev.desactivarImpagasNotifyWhatsApp,
                      }))
                    }
                  />
                  {graceWarn ? (
                    <p className="text-sm text-amber-700 dark:text-amber-300">
                      {t('pages.periodOps.graceWarn')}
                    </p>
                  ) : null}
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {t('pages.periodOps.lastAutoRun')}:{' '}
                    {formatLastRun(config.desactivarImpagasLastRunAt)}
                  </p>
                </div>
              ) : null}
            </SectionCard>
          </div>

          {canEdit ? (
            <div className="flex shrink-0 items-center justify-between gap-3 border-t border-slate-200 bg-slate-50/80 px-1 py-4 dark:border-gray-700 dark:bg-transparent">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {dirty ? t('pages.periodOps.unsavedChanges') : t('pages.periodOps.timezoneNote', { tz: draft.timezone })}
              </p>
              <Button type="submit" variant="primary" size="sm" disabled={!dirty || isSaving}>
                {isSaving ? t('common.saving') : t('pages.periodOps.configSave')}
              </Button>
            </div>
          ) : (
            <p className="pt-2 text-xs text-gray-500">{t('pages.settings.readOnly')}</p>
          )}
        </>
      )}
    </form>
  );
};
